import { Button, Tooltip } from 'antd'
import type {
  LifecycleConfirm,
  LifecycleJustify,
} from 'features/libra/lifecycle/lifecycleTypes'
import { FC, useCallback } from 'react'

import { ConfirmButton } from './ConfirmButton'
import {
  type ConfirmButtonVariant,
  getConfirmVariantProps,
} from './confirmButtonVariant'
import { JustificationButton } from './JustificationButton'

/** One lifecycle action, flattened for rendering by any action layout. */
export interface LifecycleActionButtonSpec {
  label: string
  variant: ConfirmButtonVariant
  confirm?: LifecycleConfirm
  justify?: LifecycleJustify
  /** Why the transition is held back; disables the button when set. */
  blockedReason?: string
  /** Runs the transition, with the justification if one was collected. */
  onRun: (justification?: string) => Promise<void>
}

export interface LifecycleActionButtonProps {
  action: LifecycleActionButtonSpec
  loading: boolean
  disabled: boolean
  size?: 'small' | 'middle' | 'large'
}

/**
 * Renders the dialog the action's config asks for: `justify` a justification
 * dialog, `confirm` a confirmation dialog, neither a bare button. A blocked
 * action opens no dialog — there is nothing it could complete.
 */
export const LifecycleActionButton: FC<LifecycleActionButtonProps> = ({
  action,
  loading,
  disabled,
  size = 'small',
}) => {
  const { label, variant, confirm, justify, blockedReason, onRun } = action

  // Swallow the click event; `onRun`'s first arg is the justification.
  const handleClick = useCallback(() => {
    void onRun()
  }, [onRun])

  if (blockedReason) {
    return (
      <Tooltip placement='topRight' title={blockedReason}>
        <Button size={size} {...getConfirmVariantProps(variant)} disabled>
          {label}
        </Button>
      </Tooltip>
    )
  }

  if (justify) {
    return (
      <JustificationButton
        label={label}
        variant={variant}
        dialogTitle={justify.title}
        message={justify.message}
        inputLabel={justify.inputLabel}
        placeholder={justify.placeholder}
        minLength={justify.minLength}
        maxLength={justify.maxLength}
        submitLabel={justify.submitLabel}
        size={size}
        loading={loading}
        disabled={disabled}
        onSubmit={onRun}
      />
    )
  }

  if (confirm) {
    return (
      <ConfirmButton
        label={label}
        variant={variant}
        message={confirm.message}
        confirmLabel={confirm.confirmLabel}
        dialogTitle={confirm.title}
        size={size}
        loading={loading}
        disabled={disabled}
        onConfirm={onRun}
      />
    )
  }

  return (
    <Button
      size={size}
      {...getConfirmVariantProps(variant)}
      loading={loading}
      disabled={disabled}
      onClick={handleClick}>
      {label}
    </Button>
  )
}
